/**
 * StatusBadge — task status pill for execute surfaces.
 *
 * `surface="marine"` (default): muted accent text for dark shells
 * (Complete/Deny/Track). `surface="paper"`: native-style filled /
 * outline pills for the white Task Edit sheet.
 *
 * The label comes from the native `tasks.status.*` catalogue, so the pill reads
 * in the reader's language on every surface that renders this primitive — the
 * lens lists, the kanban cards, the review modal and the execute steps. It used
 * to be hardcoded English, which showed through on the Portuguese portal.
 */
import { useTranslation } from 'react-i18next';

import type { TaskDisplayStatus } from '../types';

interface Props {
  status: TaskDisplayStatus;
  variant?: 'text' | 'pill' | 'outline' | 'filled';
  size?: 'sm' | 'md' | 'lg';
  /** Optional label override; defaults to the translated label for the status. */
  label?: string;
  /** `paper` = white Task Edit sheet; `marine` = dark execute chrome. */
  surface?: 'marine' | 'paper';
  className?: string;
}

interface StatusEntry {
  textClass: string;
  pillBgClass: string;
  outlineClass: string;
  filledClass: string;
}

const MARINE_CONFIG: Record<TaskDisplayStatus, StatusEntry> = {
  pending: {
    textClass: 'text-sky-200/85',
    pillBgClass: 'bg-sky-400/15 text-sky-200',
    outlineClass: 'border border-sky-300/40 text-sky-200',
    filledClass: 'bg-x9-info text-white',
  },
  reopened: {
    textClass: 'text-sky-200/85',
    pillBgClass: 'bg-sky-400/15 text-sky-200',
    outlineClass: 'border border-sky-300/40 text-sky-200',
    filledClass: 'bg-x9-marineSoft text-white',
  },
  accepted: {
    textClass: 'text-sky-200/85',
    pillBgClass: 'bg-sky-400/15 text-sky-200',
    outlineClass: 'border border-sky-300/40 text-sky-200',
    filledClass: 'bg-x9-info text-white',
  },
  inProgress: {
    textClass: 'text-amber-200/90',
    pillBgClass: 'bg-amber-400/15 text-amber-200',
    outlineClass: 'border border-amber-300/40 text-amber-200',
    filledClass: 'bg-x9-warning text-white',
  },
  completed: {
    textClass: 'text-emerald-200/90',
    pillBgClass: 'bg-emerald-400/15 text-emerald-200',
    outlineClass: 'border border-emerald-300/40 text-emerald-200',
    filledClass: 'bg-x9-success text-white',
  },
  denied: {
    textClass: 'text-rose-200/90',
    pillBgClass: 'bg-rose-400/15 text-rose-200',
    outlineClass: 'border border-rose-300/40 text-rose-200',
    filledClass: 'bg-x9-error text-white',
  },
};

const PAPER_CONFIG: Record<TaskDisplayStatus, StatusEntry> = {
  pending: {
    textClass: 'text-x9-info',
    pillBgClass: 'bg-x9-info/10 text-x9-info',
    outlineClass: 'border border-x9-info text-x9-info',
    filledClass: 'bg-x9-info text-white',
  },
  reopened: {
    textClass: 'text-x9-marineSoft',
    pillBgClass: 'bg-x9-marine/10 text-x9-marineSoft',
    outlineClass: 'border border-x9-marineSoft text-x9-marineSoft',
    filledClass: 'bg-x9-marineSoft text-white',
  },
  accepted: {
    textClass: 'text-x9-info',
    pillBgClass: 'bg-x9-info/10 text-x9-info',
    outlineClass: 'border border-x9-info text-x9-info',
    filledClass: 'bg-x9-info text-white',
  },
  inProgress: {
    textClass: 'text-x9-warning',
    pillBgClass: 'bg-x9-warning/10 text-x9-warning',
    outlineClass: 'border border-x9-warning text-x9-warning',
    filledClass: 'bg-x9-warning text-white',
  },
  completed: {
    textClass: 'text-x9-success',
    pillBgClass: 'bg-x9-success/10 text-x9-success',
    outlineClass: 'border border-x9-success text-x9-success',
    filledClass: 'bg-x9-success text-white',
  },
  denied: {
    textClass: 'text-x9-error',
    pillBgClass: 'bg-x9-error/10 text-x9-error',
    outlineClass: 'border border-x9-error text-x9-error',
    filledClass: 'bg-x9-error text-white',
  },
};

const SIZE_CLASSES: Record<NonNullable<Props['size']>, string> = {
  sm: 'text-[9px] tracking-[0.18em]',
  md: 'text-[10px] tracking-[0.2em]',
  lg: 'text-xs tracking-[0.22em]',
};

const PILL_SIZE_PADDING: Record<NonNullable<Props['size']>, string> = {
  sm: 'px-1.5 py-0.5',
  md: 'px-2 py-0.5',
  lg: 'px-2.5 py-1',
};

export default function StatusBadge({
  status,
  variant = 'text',
  size = 'md',
  label,
  surface = 'marine',
  className,
}: Props) {
  const { t } = useTranslation('tasks');
  const palette = surface === 'paper' ? PAPER_CONFIG : MARINE_CONFIG;
  // Rows cast a raw Firestore string to the union, so an unknown status reads
  // and colours as pending rather than printing a bare translation key.
  const key: TaskDisplayStatus = status in palette ? status : 'pending';
  const config = palette[key];
  const sizeClass = SIZE_CLASSES[size];

  const base = `inline-flex items-center font-bold uppercase ${sizeClass}`;
  let variantClasses: string;
  if (variant === 'filled') {
    variantClasses = `rounded-pill ${PILL_SIZE_PADDING[size]} ${config.filledClass}`;
  } else if (variant === 'pill') {
    variantClasses = `rounded-pill ${PILL_SIZE_PADDING[size]} ${config.pillBgClass}`;
  } else if (variant === 'outline') {
    variantClasses = `rounded-pill ${PILL_SIZE_PADDING[size]} bg-transparent ${config.outlineClass}`;
  } else {
    variantClasses = config.textClass;
  }

  return (
    <span className={`${base} ${variantClasses} ${className ?? ''}`}>
      {(label ?? t(`status.${key}`)).toUpperCase()}
    </span>
  );
}
